"use client";

import React, { useState, useEffect, useMemo } from "react";
import { useParams, useRouter } from "next/navigation";
import { useEntityView } from "@/hooks/use-entity-view";
import { isDefaultView as isDefaultViewId } from "@/hooks/use-dashboard-view";
import { useEntitiesFromDb } from "@/hooks/schema/use-entities-from-db";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { PyloAroMultiSelect } from "@/components/layouts/pylo-aro-multi-select";
import { useHasRootPriviliges } from "@/hooks/auth/use-has-root-priviliges";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import Link from "next/link";
import { Edit2, Plus, Filter } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type DragOverEvent,
  DragOverlay,
  type UniqueIdentifier,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { DroppablePalette } from "./components/droppable-palette";
import { DroppableSection } from "./components/droppable-section";
import { FieldItem } from "./components/field-item";
import { Field, Section } from "@/services/entity-view.service";
import { availableFilterOperators } from "@/hooks/use-filter";
import { FilterTrigger } from "@/components/filter/filter-trigger";
import { useEntitySchemaByName } from "@/hooks/schema/use-entity-schema-by-name";
import { FormField } from "@/hooks/schema/use-schema";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2 } from "lucide-react";
import { QueryCondition } from "@okeano-gmbh/pylo-utils";
import { FilterInput } from "@/services/entity-instance.service";
import { defaultWidthAndHeightForDataType, systemFields } from "@/lib/utils";

// Utility function to convert entity view conditions to FilterInput
export const convertEntityViewConditionToFilter = (
  condition: QueryCondition,
): FilterInput | undefined => {
  if (!condition) return undefined;

  // If it's a single condition
  if (condition.condition && condition.condition.field) {
    return {
      query: [
        {
          condition: {
            field: condition.condition.field,
            operator: condition.condition.operator,
            value: String(condition.condition.value),
          },
        },
      ],
    };
  }

  // If it's an AND condition
  if (condition.and && condition.and.length > 0) {
    const validConditions = condition.and
      .filter((andCondition) => andCondition.condition?.field)
      .map((andCondition) => ({
        condition: {
          field: andCondition.condition!.field,
          operator: andCondition.condition!.operator,
          value: String(andCondition.condition!.value),
        },
      }));

    if (validConditions.length === 0) return undefined;

    return {
      query: validConditions,
    };
  }

  // If it's an OR condition
  if (condition.or && condition.or.length > 0) {
    const validConditions = condition.or
      .filter((orCondition) => orCondition.condition?.field)
      .map((orCondition) => ({
        condition: {
          field: orCondition.condition!.field,
          operator: orCondition.condition!.operator,
          value: String(orCondition.condition!.value),
        },
      }));

    if (validConditions.length === 0) return undefined;

    return {
      query: validConditions,
    };
  }

  return undefined;
};

// Custom ConditionEditor for entity views
const EntityViewConditionEditor: React.FC<{
  query: QueryCondition;
  onChange: (query: QueryCondition) => void;
  entityName: string;
}> = ({ query, onChange, entityName }) => {
  const { entitySchemaWithRelations } = useEntitySchemaByName(entityName);

  // Ensure query is always defined
  const safeQuery = query || {
    condition: { field: "", operator: "equal", value: "" },
  };

  const handleMainConditionChange = (field: keyof any, value: any) => {
    if (safeQuery.condition) {
      const newCondition = { ...safeQuery.condition, [field]: value };
      onChange({ condition: newCondition });
    }
  };

  const addAndCondition = () => {
    const currentAndConditions = safeQuery.and || [];

    // If we have a main condition with a field, convert it to AND array
    if (safeQuery.condition && safeQuery.condition.field) {
      const newCondition: QueryCondition = {
        condition: { field: "", operator: "equal", value: "" },
      };
      onChange({
        and: [{ condition: safeQuery.condition }, newCondition],
      });
    } else if (currentAndConditions.length > 0) {
      // If we already have AND conditions, add another one
      const newCondition: QueryCondition = {
        condition: { field: "", operator: "equal", value: "" },
      };
      onChange({
        and: [...currentAndConditions, newCondition],
      });
    } else {
      // If no conditions exist, create a new main condition
      onChange({
        condition: { field: "", operator: "equal", value: "" },
      });
    }
  };

  const removeAndCondition = (index: number) => {
    const currentAndConditions = safeQuery.and || [];
    const newConditions = currentAndConditions.filter((_, i) => i !== index);

    if (newConditions.length === 1) {
      // If only one condition left, move it back to main condition
      onChange({
        condition: newConditions[0].condition,
      });
    } else if (newConditions.length === 0) {
      // If no conditions left, create empty condition
      onChange({
        condition: { field: "", operator: "equal", value: "" },
      });
    } else {
      onChange({
        and: newConditions,
      });
    }
  };

  const removeMainCondition = () => {
    // Clear the condition completely
    onChange({
      condition: { field: "", operator: "equal", value: "" },
    });
  };

  const updateAndCondition = (index: number, updatedQuery: QueryCondition) => {
    const currentAndConditions = safeQuery.and || [];
    const newConditions = [...currentAndConditions];
    newConditions[index] = updatedQuery;
    onChange({
      and: newConditions,
    });
  };

  const createFieldSelectHandler = (conditionIndex?: number) => {
    return (field: FormField) => {
      if (conditionIndex !== undefined && safeQuery.and) {
        // Update a specific condition in the AND array
        const newAndConditions = [...safeQuery.and];
        newAndConditions[conditionIndex] = {
          ...newAndConditions[conditionIndex],
          condition: {
            ...newAndConditions[conditionIndex].condition,
            field: field.name,
            operator: newAndConditions[conditionIndex].condition?.operator || "equal",
            value: newAndConditions[conditionIndex].condition?.value || "",
          },
        };
        onChange({ and: newAndConditions });
      } else if (safeQuery.condition) {
        // Update the main condition
        const newCondition = {
          ...safeQuery.condition,
          field: field.name,
          operator: safeQuery.condition.operator || "equal",
          value: safeQuery.condition.value || "",
        };
        onChange({ condition: newCondition });
      }
    };
  };

  const renderSingleCondition = (
    condition: any,
    onUpdate: (field: keyof any, value: any) => void,
    onRemove?: () => void,
    conditionIndex?: number,
  ) => {
    // Always render all conditions, whether they have fields or not
    return (
      <div className="flex items-center gap-2 rounded border bg-white p-2">
        {/* Field */}
        <FilterTrigger
          entitySchema={entitySchemaWithRelations}
          onSelect={createFieldSelectHandler(conditionIndex)}
          fieldsToHide={["created_at", "updated_at", "integer_id"]}
          amountLevels={3}
          hideListRelations={false}
        >
          <div className="flex-1 rounded border px-3 py-2 text-sm">
            {condition.field || "Select field..."}
          </div>
        </FilterTrigger>

        {/* Operator */}
        <Select value={condition.operator} onValueChange={(value) => onUpdate("operator", value)}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Operator" />
          </SelectTrigger>
          <SelectContent>
            {availableFilterOperators.map((operator: any) => (
              <SelectItem key={operator.value} value={operator.value}>
                {operator.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Value */}
        <Input
          value={
            typeof condition.value === "string" || typeof condition.value === "number"
              ? String(condition.value)
              : ""
          }
          onChange={(e) => onUpdate("value", e.target.value)}
          placeholder="Value"
          className="flex-1"
        />

        {/* Remove button */}
        {onRemove && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="text-red-500 hover:text-red-700"
          >
            <Trash2 size={16} />
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {safeQuery.and && safeQuery.and.length > 0 ? (
        /* Multiple conditions grouped with AND */
        <div className="relative rounded-lg border border-gray-200 bg-gray-50/50 p-4">
          {/* All conditions */}
          <div className="relative flex flex-row">
            <div className="flex min-h-full flex-col items-center">
              <div className="absolute left-6 top-0 h-0.5 w-8 bg-cyan-200"></div>
              <div className="w-0.5 flex-1 bg-cyan-200"></div>
              <span className="inline-flex items-center rounded-full bg-cyan-100 px-3 py-1 text-sm font-medium text-cyan-800">
                and
              </span>
              <div className="w-0.5 flex-1 bg-cyan-200"></div>
              <div className="absolute bottom-0 left-6 h-0.5 w-8 bg-cyan-200"></div>
            </div>
            <div className="flex flex-col gap-y-3">
              {safeQuery.and.map((andCondition, index) => (
                <div key={`and-condition-${index}`} className="relative">
                  <div className="ml-8">
                    {andCondition.condition &&
                      renderSingleCondition(
                        andCondition.condition,
                        (field, value) =>
                          updateAndCondition(index, {
                            condition: {
                              ...andCondition.condition!,
                              [field]: value,
                            },
                          }),
                        safeQuery.and!.length > 1 ? () => removeAndCondition(index) : undefined,
                        index,
                      )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Add another condition button */}
          <div className="mt-4 pl-6">
            <Button variant="outline" size="sm" onClick={addAndCondition} className="ml-8">
              <Plus size={16} className="mr-1" />
              Add Condition
            </Button>
          </div>
        </div>
      ) : (
        /* Single condition */
        <div className="space-y-2">
          {safeQuery.condition &&
            renderSingleCondition(
              safeQuery.condition,
              handleMainConditionChange,
              safeQuery.condition.field ? removeMainCondition : undefined,
              undefined,
            )}

          {/* Add condition button */}
          <Button variant="outline" size="sm" onClick={addAndCondition} className="mt-2">
            <Plus size={16} className="mr-1" />
            Add Condition
          </Button>
        </div>
      )}
    </div>
  );
};

export function EditEntityView({
  viewId: propViewId,
  entityId: propEntityId,
  onSave,
}: {
  viewId?: string;
  entityId?: string;
  onSave?: () => void;
} = {}) {
  const params = useParams();

  // Use props if provided, otherwise fall back to useParams
  const viewId = propViewId || (params.viewId as string);
  const id = propEntityId || (params.id as string);

  // const { updateEntityViewAsync, isUpdating, entityViews } = useEntityViewList(
  //   id as string,
  // );

  const { entityView, isUpdating: isViewUpdating, updateEntityViewAsync } = useEntityView(viewId);

  const { availableEntities } = useEntitiesFromDb();
  const entity = availableEntities?.find((e) => e?.id === id);

  // Convert entity fields to our Field type
  const entityFields: Field[] = useMemo(() => {
    if (!entity?.entity_fields?.data) return [];

    return entity.entity_fields.data
      .filter((field) => !systemFields.includes(field.name))
      .map((field) => ({
        id: field.id,
        name: field.name,
        type: field.data_type,
        icon: field.form_type ?? field.data_type,
        width: defaultWidthAndHeightForDataType(field.data_type).width,
        height: defaultWidthAndHeightForDataType(field.data_type).height,
      }));
  }, [entity?.entity_fields?.data]);

  // Convert entity relations to our Field type
  const entityRelations: Field[] = useMemo(() => {
    if (!entity?.entity_relations?.data) return [];

    return entity.entity_relations.data.map((relation) => ({
      id: relation.id,
      name: relation.field_name,
      type: "relation",
      icon: "relation",
      width: 3,
      height: 1,
    }));
  }, [entity?.entity_relations?.data]);

  const entityRelated: Field[] = useMemo(() => {
    if (!entity?.entity_related?.data) return [];

    return entity.entity_related.data.map((relation) => ({
      id: relation.id,
      name: relation.target_field_name ?? "",
      type: "relation", // Keep as 'relation' but we'll handle it differently
      icon: "relation",
      width: 3,
      height: 1,
    }));
  }, [entity?.entity_related?.data]);

  // Combine fields and relations for the palette
  const allAvailableFields = useMemo(() => {
    return [...entityFields, ...entityRelations, ...entityRelated];
  }, [entityFields, entityRelations, entityRelated]);

  const [sections, setSections] = useState<Section[]>([]);
  const [availableFields, setAvailableFields] = useState<Field[]>(allAvailableFields);

  // Update available fields when entity data changes
  useEffect(() => {
    const fieldsAlreadyInSections = sections.flatMap((section) => section.fields);
    setAvailableFields(
      allAvailableFields.filter((field) => !fieldsAlreadyInSections.some((f) => f.id === field.id)),
    );
  }, [allAvailableFields, sections]);

  const [newSectionName, setNewSectionName] = useState("");
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [editingSectionName, setEditingSectionName] = useState("");
  const [isAddSectionOpen, setIsAddSectionOpen] = useState(false);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [draggedField, setDraggedField] = useState<Field | null>(null);
  const [hoveredSectionId, setHoveredSectionId] = useState<string | null>(null);
  const [isDraggingAny, setIsDraggingAny] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const [viewTitle, setViewTitle] = useState(entityView?.name);
  const [viewDescription, setViewDescription] = useState(entityView?.description);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedTitle, setEditedTitle] = useState(viewTitle);
  const [editedDescription, setEditedDescription] = useState(viewDescription);

  // Condition state
  const [condition, setCondition] = useState<QueryCondition>({
    condition: { field: "", operator: "equal", value: "" },
  });
  const [isConditionOpen, setIsConditionOpen] = useState(false);

  // ARO-related state
  const { hasRootPriviliges: mayEdit } = useHasRootPriviliges();
  const [selectedAros, setSelectedAros] = useState<Array<{ id: string }>>([]);

  const router = useRouter();

  // Update local state when entity view data changes
  useEffect(() => {
    if (entityView) {
      setViewTitle(entityView.name);
      setEditedTitle(entityView.name);
      setViewDescription(entityView.description ?? "");
      setEditedDescription(entityView.description ?? "");
      setSections(entityView?.section_config || []);
      setSelectedAros(entityView?.pylo_aros ?? []);

      // Parse condition from entity view
      if (entityView.condition) {
        try {
          const parsedCondition = JSON.parse(entityView.condition);
          setCondition(parsedCondition);
        } catch (error) {
          console.error("Failed to parse condition:", error);
          setCondition({
            condition: { field: "", operator: "equal", value: "" },
          });
        }
      }
    }
  }, [entityView]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
    setIsDraggingAny(true);

    // Get field data from the event
    const fieldData = event.active.data.current;
    if (fieldData) {
      setDraggedField(fieldData.field);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;

    if (!over) {
      setHoveredSectionId(null);
      return;
    }

    // Handle dragging over the palette
    if (over.id === "palette") {
      setHoveredSectionId("palette");
      return;
    }

    // Handle dragging over a section
    if (over.id.toString().startsWith("section-")) {
      setHoveredSectionId(over.id.toString());
    } else {
      // Handle dragging over a field - highlight its section
      const fieldId = over.id.toString();
      const sectionId = findSectionByFieldId(fieldId)?.id || null;
      setHoveredSectionId(sectionId);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveId(null);
    setDraggedField(null);
    setHoveredSectionId(null);
    setIsDraggingAny(false);

    if (!over) return;

    const activeData = active.data.current as {
      field: Field;
      fromPalette: boolean;
      sectionId?: string;
    };
    if (!activeData) return;

    const activeField = activeData.field;
    const fromPalette = activeData.fromPalette;
    const sourceSectionId = activeData.sectionId;

    // Handle dropping back to palette
    if (over.id === "palette") {
      if (!fromPalette && sourceSectionId) {
        // Remove field from section and add back to palette
        const fieldToReturn = sections
          .find((s) => s.id === sourceSectionId)
          ?.fields.find((f) => f.id === active.id);

        if (fieldToReturn) {
          // Add the field back to available fields
          setAvailableFields((prev) => [...prev, fieldToReturn]);

          // Remove from section
          setSections((prev) =>
            prev.map((section) =>
              section.id === sourceSectionId
                ? {
                    ...section,
                    fields: section.fields.filter((f) => f.id !== active.id),
                  }
                : section,
            ),
          );
        }
      }
      return;
    }

    // Get target section ID - either directly from the section or from the field being dropped on
    const targetSectionId = over.id.toString().startsWith("section-")
      ? over.id.toString()
      : findSectionByFieldId(over.id.toString())?.id;

    if (!targetSectionId) return;

    // First, remove the field from its source (either palette or section)
    if (fromPalette) {
      setAvailableFields((prev) => prev.filter((f) => f.id !== active.id));
    } else if (sourceSectionId) {
      setSections((prev) =>
        prev.map((section) =>
          section.id === sourceSectionId
            ? {
                ...section,
                fields: section.fields.filter((f) => f.id !== active.id),
              }
            : section,
        ),
      );
    }

    // Then, add the field to the target section
    setSections((prev) =>
      prev.map((section) => {
        if (section.id === targetSectionId) {
          // If dropping on a field, insert at that position
          if (!over.id.toString().startsWith("section-")) {
            const targetIndex = section.fields.findIndex((f) => f.id === over.id);
            const newFields = [...section.fields];
            newFields.splice(targetIndex, 0, activeField);
            return { ...section, fields: newFields };
          }
          // Otherwise append to the end
          return { ...section, fields: [...section.fields, activeField] };
        }
        return section;
      }),
    );

    // Handle reordering within palette
    if (fromPalette && over.data.current?.fromPalette) {
      const oldIndex = availableFields.findIndex((f) => f.id === active.id);
      const newIndex = availableFields.findIndex((f) => f.id === over.id);

      if (oldIndex !== newIndex) {
        setAvailableFields((prev) => arrayMove(prev, oldIndex, newIndex));
      }
    }
  };

  const findFieldById = (id: string): Field | null => {
    for (const section of sections) {
      const field = section.fields.find((f) => f.id === id);
      if (field) return field;
    }
    return availableFields.find((f) => f.id === id) || null;
  };

  const findSectionByFieldId = (fieldId: string): Section | null => {
    return sections.find((section) => section.fields.some((f) => f.id === fieldId)) || null;
  };

  const addSection = () => {
    if (!newSectionName.trim()) return;

    const newSection: Section = {
      id: `section-${Date.now()}`,
      name: newSectionName,
      fields: [],
    };

    setSections((prev) => [...prev, newSection]);
    setNewSectionName("");
    setIsAddSectionOpen(false);
  };

  const deleteSection = (sectionId: string) => {
    setSections((prev) => prev.filter((s) => s.id !== sectionId));
  };

  const startEditingSection = (section: Section) => {
    setEditingSectionId(section.id);
    setEditingSectionName(section.name);
  };

  const saveEditingSection = () => {
    if (!editingSectionName.trim()) return;

    setSections((prev) =>
      prev.map((section) =>
        section.id === editingSectionId ? { ...section, name: editingSectionName } : section,
      ),
    );
    setEditingSectionId(null);
    setEditingSectionName("");
  };

  const removeFieldFromSection = (sectionId: string, fieldId: string) => {
    // Find the field to be removed
    const fieldToReturn = sections
      .find((s) => s.id === sectionId)
      ?.fields.find((f) => f.id === fieldId);

    if (fieldToReturn) {
      // Add the field back to available fields
      setAvailableFields((prev) => [...prev, fieldToReturn]);

      // Remove from section
      setSections((prev) =>
        prev.map((section) =>
          section.id === sectionId
            ? {
                ...section,
                fields: section.fields.filter((f) => f.id !== fieldId),
              }
            : section,
        ),
      );
    }
  };

  const updateFieldWidth = (sectionId: string, fieldId: string, width: 1 | 2 | 3) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              fields: section.fields.map((field) =>
                field.id === fieldId ? { ...field, width } : field,
              ),
            }
          : section,
      ),
    );
  };

  const updateFieldHeight = (sectionId: string, fieldId: string, height: 1 | 2 | 3) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              fields: section.fields.map((field) =>
                field.id === fieldId ? { ...field, height } : field,
              ),
            }
          : section,
      ),
    );
  };

  const updateRelationFieldDisplay = (
    sectionId: string,
    fieldId: string,
    relationFieldToDisplay: string,
  ) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              fields: section.fields.map((field) =>
                field.id === fieldId
                  ? {
                      ...field,
                      relation_field_to_display: relationFieldToDisplay,
                    }
                  : field,
              ),
            }
          : section,
      ),
    );
  };

  const activeField = activeId ? findFieldById(activeId.toString()) : null;

  const handleTitleSubmit = () => {
    if (editedTitle?.trim() && editedTitle !== viewTitle) {
      setViewTitle(editedTitle.trim());
    }
    setIsEditingTitle(false);
  };

  const handleDescriptionSubmit = () => {
    if (editedDescription?.trim() !== viewDescription) {
      setViewDescription(editedDescription?.trim());
    }
    setIsEditingDescription(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent, type: "title" | "description") => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (type === "title") {
        handleTitleSubmit();
      } else {
        handleDescriptionSubmit();
      }
    } else if (e.key === "Escape") {
      if (type === "title") {
        setEditedTitle(viewTitle);
        setIsEditingTitle(false);
      } else {
        setEditedDescription(viewDescription);
        setIsEditingDescription(false);
      }
    }
  };

  const handleSaveView = async () => {
    if (viewId && !isDefaultViewId(viewId)) {
      await updateEntityViewAsync({
        id: viewId as string,
        name: viewTitle,
        description: viewDescription || undefined,
        section_config: sections,
        condition: JSON.stringify(condition),
        pylo_aros: selectedAros.map((a) => ({ id: a.id })), // Pass only IDs as objects
      });

      // Use onSave callback if provided (modal context), otherwise navigate
      if (onSave) {
        onSave();
      } else {
        router.push(`/p/settings/entities?entityId=${id}&tab=views`);
      }
    }
  };

  // Add moveSection function
  const moveSection = (sectionId: string, direction: "up" | "down") => {
    setSections((prev) => {
      const currentIndex = prev.findIndex((s) => s.id === sectionId);
      if (currentIndex === -1) return prev;

      const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
      if (newIndex < 0 || newIndex >= prev.length) return prev;

      return arrayMove(prev, currentIndex, newIndex);
    });
  };

  if (!isMounted) {
    return null;
  }

  const isDefaultView = isDefaultViewId(viewId);

  return (
    <div className="flex min-h-0 grow flex-col overflow-hidden bg-gray-50 p-6">
      <div className="flex min-h-0 grow flex-col">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="grow space-y-1">
            {isEditingTitle && !isDefaultView ? (
              <input
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                onBlur={handleTitleSubmit}
                onKeyDown={(e) => handleKeyDown(e, "title")}
                className="m-0 w-full border-none bg-transparent p-0 text-2xl font-bold outline-none"
                autoFocus
                disabled={isViewUpdating}
              />
            ) : (
              <h1
                onClick={() => !isViewUpdating && !isDefaultView && setIsEditingTitle(true)}
                className={`group flex cursor-pointer items-center gap-2 text-2xl font-bold text-gray-900 ${
                  isViewUpdating || isDefaultView ? "cursor-not-allowed opacity-50" : ""
                }`}
              >
                {viewTitle}
                {!isViewUpdating && !isDefaultView && (
                  <div className="opacity-0 group-hover:opacity-100">
                    <Edit2 className="h-4 w-4 text-gray-500" />
                  </div>
                )}
              </h1>
            )}
            {isEditingDescription && !isDefaultView ? (
              <input
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                onBlur={handleDescriptionSubmit}
                onKeyDown={(e) => handleKeyDown(e, "description")}
                placeholder="Enter a description..."
                autoFocus
                className="m-0 h-6 w-full border-none bg-transparent p-0 outline-none"
                disabled={isViewUpdating}
              />
            ) : (
              <div
                onClick={() => !isViewUpdating && !isDefaultView && setIsEditingDescription(true)}
                className={`group flex h-6 cursor-pointer items-center gap-2 whitespace-pre-wrap text-gray-600 ${
                  isViewUpdating || isDefaultView ? "cursor-not-allowed opacity-50" : ""
                }`}
              >
                {viewDescription}
                {!isViewUpdating && !isDefaultView && (
                  <div className="opacity-0 group-hover:opacity-100">
                    <Edit2 className="h-3 w-3 text-gray-500" />
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {onSave ? (
              <Button variant="outline" onClick={onSave}>
                Cancel
              </Button>
            ) : (
              <Button variant="outline" asChild>
                <Link href={`/p/settings/entities?entityId=${id}&tab=views`}>Cancel</Link>
              </Button>
            )}
            {!isDefaultView && (
              <Button
                onClick={handleSaveView}
                disabled={isViewUpdating || (!entityView && !viewTitle)}
              >
                Save View
              </Button>
            )}
          </div>
        </div>

        {/* Settings Toggle */}
        <div className="mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsConditionOpen(!isConditionOpen)}
            className="flex items-center gap-2"
          >
            <Filter className="h-4 w-4" />
            Conditional settings
          </Button>
        </div>

        {/* Settings Section (ARO Selection + Condition) */}
        {isConditionOpen && (
          <div className="mb-6 space-y-6">
            {/* ARO Selection */}
            {mayEdit && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Who can view this entity view?</label>
                <PyloAroMultiSelect
                  selectedAros={selectedAros}
                  onSelectionChange={setSelectedAros}
                  placeholder="Select users that can view this entity view..."
                />
              </div>
            )}

            {/* Condition Section */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Filter conditions for this view</label>
              <Card className="p-4">
                <EntityViewConditionEditor
                  query={condition}
                  onChange={setCondition}
                  entityName={entity?.name || ""}
                />
              </Card>
            </div>
          </div>
        )}

        <DndContext
          sensors={isDefaultView ? [] : sensors}
          collisionDetection={closestCenter}
          onDragStart={isDefaultView ? undefined : handleDragStart}
          onDragOver={isDefaultView ? undefined : handleDragOver}
          onDragEnd={isDefaultView ? undefined : handleDragEnd}
        >
          <div className="grid min-h-0 grow grid-cols-1 gap-6 lg:grid-cols-4">
            {/* Field Palette */}
            <div className="h-full overflow-y-auto lg:col-span-1">
              <DroppablePalette
                availableFields={availableFields}
                isHovered={hoveredSectionId === "palette"}
                isDraggingAny={isDraggingAny}
              />
            </div>

            {/* Main Content Area */}
            <div className="min-h-0 space-y-6 overflow-y-auto lg:col-span-3">
              {sections.map((section, index) => (
                <DroppableSection
                  key={section.id}
                  section={section}
                  onEditSection={startEditingSection}
                  onDeleteSection={deleteSection}
                  onRemoveField={removeFieldFromSection}
                  onUpdateFieldWidth={updateFieldWidth}
                  onUpdateFieldHeight={updateFieldHeight}
                  onUpdateRelationFieldDisplay={updateRelationFieldDisplay}
                  onMoveSection={moveSection}
                  isFirst={index === 0}
                  isLast={index === sections.length - 1}
                  isHovered={hoveredSectionId === section.id}
                  isDraggingAny={isDraggingAny}
                  draggedField={draggedField}
                  entityName={entity?.name || ""}
                  entityRelations={entity?.entity_relations?.data}
                  entityRelated={entity?.entity_related?.data}
                />
              ))}

              {/* Add Section Button */}
              <Dialog open={isAddSectionOpen} onOpenChange={setIsAddSectionOpen}>
                <DialogTrigger asChild>
                  <Card className="cursor-pointer border-2 border-dashed border-green-300 bg-green-50 transition-colors hover:bg-green-100">
                    <CardContent className="flex items-center justify-center py-8">
                      <div className="text-center text-green-700">
                        <Plus className="mx-auto mb-2 h-6 w-6" />
                        <div className="font-medium">Add New Section</div>
                      </div>
                    </CardContent>
                  </Card>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Section</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="section-name">Section Name</Label>
                      <Input
                        id="section-name"
                        value={newSectionName}
                        onChange={(e) => setNewSectionName(e.target.value)}
                        placeholder="Enter section name..."
                        onKeyDown={(e) => {
                          if (e.key === "Enter") addSection();
                        }}
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setIsAddSectionOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={addSection} disabled={!newSectionName.trim()}>
                        Add Section
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {editingSectionId && (
                <Dialog
                  open={!!editingSectionId}
                  onOpenChange={(open) => !open && setEditingSectionId(null)}
                >
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Edit Section</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="edit-section-name">Section Name</Label>
                        <Input
                          id="edit-section-name"
                          value={editingSectionName}
                          onChange={(e) => setEditingSectionName(e.target.value)}
                          placeholder="Enter section name..."
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveEditingSection();
                          }}
                          autoFocus
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setEditingSectionId(null)}>
                          Cancel
                        </Button>
                        <Button onClick={saveEditingSection} disabled={!editingSectionName.trim()}>
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>

          <DragOverlay>{activeField ? <FieldItem field={activeField} /> : null}</DragOverlay>
        </DndContext>
      </div>
    </div>
  );
}
